const loadBraintree = () =>
  Promise.all([
    import('braintree-web/client'),
    import('braintree-web/three-d-secure'),
    import('braintree-web/hosted-fields'),
  ]);

import { kebabCase, toLower } from 'lodash';
import { Client, ThreeDSecure, HostedFields, HostedFieldFieldOptions, ThreeDSecureParameters } from 'braintree-web';
import { compose, curriedMap, curriedMapValues, curriedPick } from '@adjust/dashboard-core/lib/fp';

export enum PaymentTheme {
  Light = 'light',
  Dark = 'dark',
}

interface FieldsTheme {
  input: string;
  placeholder: string;
  placeholderHover: string;
}

type HostedFieldsThemes = {
  [key in PaymentTheme]: FieldsTheme;
};

type StyleRule = Record<string, string | number>;

type Style = Record<string, string | number | StyleRule | Record<string, StyleRule>>;

export const themes: HostedFieldsThemes = {
  light: {
    input: '#fff',
    placeholder: '#96a1bd',
    placeholderHover: '#fff',
  },
  dark: {
    input: '#4b577e',
    placeholder: '#8897ae',
    placeholderHover: '#8897ae',
  },
};

export const fields: HostedFieldFieldOptions = {
  number: {
    selector: '#card-number',
    placeholder: '4111 1111 1111 1111',
  },
  cvv: {
    selector: '#security-code',
    placeholder: '123',
  },
  expirationDate: {
    selector: '#expiration-date',
    placeholder: 'MM/YYYY',
  },
};

export const getStyles = (theme: PaymentTheme): Style => ({
  input: {
    'font-size': '13px',
    color: themes[theme].input,
  },
  '::-webkit-input-placeholder': {
    color: themes[theme].placeholder,
  },
  'input:focus::-webkit-input-placeholder': {
    color: themes[theme].placeholderHover,
  },
  '::-moz-placeholder': {
    color: themes[theme].placeholder,
  },
  'input:focus::-moz-placeholder': {
    color: themes[theme].placeholderHover,
  },
  ':-moz-placeholder': {
    color: themes[theme].placeholder,
  },
  'input:focus:-moz-placeholder': {
    color: themes[theme].placeholderHover,
  },
  ':-ms-input-placeholder': {
    color: themes[theme].placeholder,
  },
  'input:focus:-ms-input-placeholder': {
    color: themes[theme].placeholderHover,
  },
});

export const normalizeCreditCard = compose(kebabCase, toLower);

export const createInstances = async (token: string, theme: PaymentTheme): Promise<[ThreeDSecure, HostedFields]> => {
  const [client, threeDSecure, hostedFields] = await loadBraintree();
  const clientInstance: Client = await client.create({
    authorization: token,
  });
  const threeDSecureInstance: ThreeDSecure = await threeDSecure.create({
    version: 2,
    client: clientInstance,
  });
  const hostedFieldsInstance: HostedFields = await hostedFields.create({
    client: clientInstance,
    fields,
    styles: getStyles(theme),
  });

  return Promise.all([threeDSecureInstance, hostedFieldsInstance]);
};

export const onLookupComplete = (data: any, next: any): any => next();

export const getSupportedCardTypes = threeDSecureInstance => {
  const { supportedCardTypes } =
    threeDSecureInstance._framework._client.getConfiguration().gatewayConfiguration.creditCards;

  return curriedMap(normalizeCreditCard)(supportedCardTypes);
};

export const verifyCard = async (
  threeDSecureInstance: ThreeDSecure,
  { nonce, details, billingInfo },
): Promise<string> => {
  const threeDSecureParameters: ThreeDSecureParameters = {
    nonce,
    amount: '1.00',
    challengeRequested: true,
    bin: details.bin,
    email: billingInfo.email,
    billingAddress: billingInfo.billingAddress,
    additionalInformation: billingInfo.additionalInformation,
    onLookupComplete,
  };

  const {
    threeDSecureInfo: { liabilityShifted, liabilityShiftPossible, status },
    ...result
  } = await threeDSecureInstance.verifyCard(threeDSecureParameters);

  if (liabilityShiftPossible && !liabilityShifted) {
    // The user either canceled or couldn't verify the card, so we can not save it
    throw new Error(`3Ds verification failed: ${status}`);
  }

  return result.nonce;
};

export const teardown = async (hostedFieldsInstance: HostedFields): Promise<void> => hostedFieldsInstance.teardown();

export const normalizeValidationFields = curriedMapValues(curriedPick(['isEmpty', 'isValid', 'isPotentiallyValid']));

export const getCreditCardType = (cards: any): string => (cards && cards.length === 1 ? cards[0].niceType : '');

export const getBraintreeErrorMessage = (err: any): string => {
  if (err.details?.originalError) {
    const parentMessage = getBraintreeErrorMessage(err.details.originalError);
    return `${err.message}\nDetails: ${parentMessage}`;
  }

  return err.description || err.error?.message || err.message;
};
